import { HttpClient, HttpEvent, HttpParams, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { enpointCervinoWeb } from 'src/app/configuration/enpointsCervino';
import { ItemFilterModel } from 'src/app/models/filters/item-filter.model';
import { MaintenanceOrderImportModel } from 'src/app/models/imports/maintenance-order-import.model';
import { LoadPreInvoicingModel } from 'src/app/models/invoicing/load-preinvoicing.models';
import { PreInvoicingAlertModel } from 'src/app/models/invoicing/pre-invoicing-alert.models';
import { EquipmentInWorkshopTableModel } from 'src/app/models/maintenance/equipment-in-workshop-table.model';
import { MaintenanceOrderPrintModel } from 'src/app/models/maintenance/maintenance-order-print.model';
import { MaintenanceOrderModel } from 'src/app/models/maintenance/maintenance-order.model';
import { SuspensionModel } from 'src/app/models/rental/suspension.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class MaintenanceOrderService {

  constructor(private _httpClient: HttpClient) { }

  annulMaintenanceOrderAsync(maintenanceOrderId: number): Observable<MaintenanceOrderModel> {
    const params = new HttpParams().set('maintenanceOrderId', maintenanceOrderId);
    return this._httpClient.put<MaintenanceOrderModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceOrder.AnnulMaintenanceOrderAsync}`,
      null, { params: params }
    );
  }

  attachFilesToMaintenanceOrder(maintenanceOrderId: number, files: FormData): Observable<boolean> {
    return this._httpClient.post<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceOrder.AttachFilesToMaintenanceOrderAsync}/${maintenanceOrderId}`, files);
  }

  blockMaintenanceOrderAsync(maintenanceOrderId: number): Observable<MaintenanceOrderModel> {
    const params = new HttpParams().set('maintenanceOrderId', maintenanceOrderId);
    return this._httpClient.put<MaintenanceOrderModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceOrder.BlockMaintenanceOrderAsync}`,
      null, { params: params }
    );
  }

  createMaintenanceOrder(maintenanceOrderContractModel: MaintenanceOrderModel): Observable<MaintenanceOrderModel> {
    return this._httpClient.post<MaintenanceOrderModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceOrder.CreateAsync}`,
      maintenanceOrderContractModel);
  }

  createRescheduledMaintenanceOrder(maintenanceOrderId: number): Observable<MaintenanceOrderModel> {
    return this._httpClient.post<MaintenanceOrderModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceOrder.CreateRescheduledMaintenanceOrderAsync}`,
      maintenanceOrderId);
  }

  deleteMaintenanceOrder(maintenanceOrderId: number): Observable<MaintenanceOrderModel> {
    return this._httpClient.delete<MaintenanceOrderModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceOrder.DeleteAsync}/${maintenanceOrderId}`);
  }

  deleteFilesToMaintenanceOrder(maintenanceOrderId: number, file: string): Observable<Array<string>> {
    const itemFilterResponse = new ItemFilterModel("file", null, maintenanceOrderId, file);
    return this._httpClient.post<Array<string>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceOrder.DeleteFileToMaintenanceOrderAsync}`,
      itemFilterResponse);
  }

  downloadFileToMaintenanceOrder(maintenanceOrderId: number, file: string): Observable<HttpEvent<Blob>> {
    const itemFilterResponse = new ItemFilterModel("file", null, maintenanceOrderId, file);
    return this._httpClient.request(new HttpRequest('POST', `${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceOrder.DownloadFileToMaintenanceOrderAsync}`,
      itemFilterResponse, { reportProgress: true, responseType: 'blob' }));
  }

  downloadGuideImport(): Observable<HttpEvent<Blob>> {
    return this._httpClient.request(new HttpRequest('GET', `${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceOrder.DownloadGuideImport}`,
      null, { reportProgress: true, responseType: 'blob' }));
  }

  exportGeneralConsultation(filters: ColumnResponseModel[], columnSort: string | null, ascendant: boolean): Observable<Blob> {
    const params = new HttpParams()
      .set('columnSort', columnSort ?? '')
      .set('ascendant', ascendant.toString());
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceOrder.ExportGeneralConsultation}`, filters, {
      params,
      responseType: 'blob',
    });
  }

  exportImportErrors(alerts: PreInvoicingAlertModel[]): Observable<Blob> {
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceOrder.ExportImportErrors}`, alerts,
      {
        responseType: 'blob'
      }
    );
  }

  manageSuspensionAsync(maintenanceOrderId: number): Observable<SuspensionModel> {
    return this._httpClient.post<SuspensionModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceOrder.ManageSuspensionAsync}/${maintenanceOrderId}`,
      null
    );
  }

  getFilesToMaintenanceOrder(maintenanceOrderId: number): Observable<any[]> {
    return this._httpClient.get<any[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceOrder.GetFilesToMaintenanceOrderAsync}/${maintenanceOrderId}`,
    );
  }

  getAllCostCentersForSelectMaintenanceOrderAsync(branchId: number, clientId: number): Observable<Array<any>> {
    const params = new HttpParams()
      .set('branchId', branchId)
      .set('clientId', clientId);
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceOrder.GetAllCostCentersForSelectMaintenanceOrderAsync}`, { params });
  }

  getAllMaintenanceOrdersWithPagination(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<any>> {
    const params = new HttpParams()
      .set('page', page)
      .set('limit', limit)
      .set('columnSort', columnSort || '')
      .set('ascendant', ascendant);
    return this._httpClient.post<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceOrder.GetAllMaintenanceOrdersWithPaginationAsync}`,
      filters, { params: params }
    );
  }

  getColumnsForAttachmentsAsync(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceOrder.GetColumnsForAttachmentsAsync}`);
  }

  getColumnsForEquipmentAsync(maintenanceOrderTypeId: number): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceOrder.GetColumnsForEquipmentAsync}/${maintenanceOrderTypeId}`);
  }

  getColumnsForGeneralConsultation(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceOrder.GetColumnsForGeneralConsultationAsync}`);
  }

  getCurrentNoteStage(maintenanceOrderId: number): Observable<string> {
    return this._httpClient.get<{ value: string }>(`${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceOrder.GetCurrentNoteStage}/${maintenanceOrderId}`)
      .pipe(map(response => response.value));
  }

  getCurrentHourometerAsync(branchId: number, articleId: number): Observable<number> {
    return this._httpClient.get<number>(`${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceOrder.GetCurrentHourometerAsync}/${branchId}/${articleId}`);
  }

  getEquipmentAsync(page: number, limit: number, columnSort: string | null, ascendant: boolean,
    costCenterId: number, maintenanceOrderTypeId: number, filters: ColumnResponseModel[]): Observable<PagedResponseModel<EquipmentInWorkshopTableModel>> {
    const params = new HttpParams()
      .set('page', page)
      .set('limit', limit)
      .set('columnSort', columnSort || '')
      .set('ascendant', ascendant)
      .set('costCenterId', costCenterId)
      .set('maintenanceOrderTypeId', maintenanceOrderTypeId);
    return this._httpClient.post<PagedResponseModel<EquipmentInWorkshopTableModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceOrder.GetEquipmentAsync}`,
      filters, { params: params }
    );
  }

  getFirstMaintenanceOrderByKeysAsync(branchId: number, maintenanceOrderClassId: number): Observable<MaintenanceOrderModel> {
    const params = new HttpParams().set('branchId', branchId).set('maintenanceOrderClassId', maintenanceOrderClassId);
    return this._httpClient.get<MaintenanceOrderModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceOrder.GetFirstMaintenanceOrderByKeysAsync}`,
      { params: params }
    );
  }

  getLastMaintenanceOrderByKeysAsync(branchId: number, maintenanceOrderClassId: number): Observable<MaintenanceOrderModel> {
    const params = new HttpParams().set('branchId', branchId).set('maintenanceOrderClassId', maintenanceOrderClassId);
    return this._httpClient.get<MaintenanceOrderModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceOrder.GetLastMaintenanceOrderByKeysAsync}`,
      { params: params }
    );
  }

  getMaintenanceOrderById(maintenanceOrderId: number): Observable<MaintenanceOrderModel> {
    return this._httpClient.get<MaintenanceOrderModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceOrder.GetByIdAsync}/${maintenanceOrderId}`);
  }

  getMaintenanceOrderByKeysAsync(consecutive: number, branchId: number, maintenanceOrderClassId: number): Observable<MaintenanceOrderModel> {
    const params = new HttpParams().set('consecutive', consecutive).set('branchId', branchId).set('maintenanceOrderClassId', maintenanceOrderClassId);
    return this._httpClient.get<MaintenanceOrderModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceOrder.GetMaintenanceOrderByKeysAsync}`,
      { params: params }
    );
  }

  getMaintenanceOrderByKeysByPrintAsync(consecutive: number, branchId: number, maintenanceOrderClassId: number): Observable<MaintenanceOrderPrintModel> {
    const params = new HttpParams().set('consecutive', consecutive).set('branchId', branchId).set('maintenanceOrderClassId', maintenanceOrderClassId);
    return this._httpClient.get<MaintenanceOrderPrintModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceOrder.GetMaintenanceOrderByKeysByPrintAsync}`,
      { params: params }
    );
  }

  getMaintenanceOrderScheduledByKeysByPrintAsync(consecutive: number, branchId: number, printBlank: boolean): Observable<MaintenanceOrderPrintModel> {
    const params = new HttpParams().set('consecutive', consecutive).set('branchId', branchId).set('printBlank', printBlank);
    return this._httpClient.get<MaintenanceOrderPrintModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceOrder.GetMaintenanceOrderScheduledByKeysByPrintAsync}`,
      { params: params }
    );
  }

  getNextMaintenanceOrderByKeysAsync(consecutive: number, branchId: number, maintenanceOrderClassId: number): Observable<MaintenanceOrderModel> {
    const params = new HttpParams().set('consecutive', consecutive).set('branchId', branchId).set('maintenanceOrderClassId', maintenanceOrderClassId);
    return this._httpClient.get<MaintenanceOrderModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceOrder.GetNextMaintenanceOrderByKeysAsync}`,
      { params: params }
    );
  }

  getPreviusMaintenanceOrderByKeysAsync(consecutive: number, branchId: number, maintenanceOrderClassId: number): Observable<MaintenanceOrderModel> {
    const params = new HttpParams().set('consecutive', consecutive).set('branchId', branchId).set('maintenanceOrderClassId', maintenanceOrderClassId);
    return this._httpClient.get<MaintenanceOrderModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceOrder.GetPreviusMaintenanceOrderByKeysAsync}`,
      { params: params }
    );
  }

  getSuspensionByMaintenanceOrderAsync(maintenanceOrderId: number): Observable<SuspensionModel | null> {
    return this._httpClient.get<SuspensionModel | null>(`${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceOrder.GetSuspensionByMaintenanceOrderAsync}/${maintenanceOrderId}`);
  }

  getTotalActivitiesRescheduledAsync(maintenanceOrderId: number): Observable<number> {
    const params = new HttpParams().set('maintenanceOrderId', maintenanceOrderId);
    return this._httpClient.get<number>(`${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceOrder.GetTotalActivitiesRescheduledAsync}`,
      { params: params }
    );
  }

  hasExistingMaintenanceOrder(branchId: number, maintenanceOrderClassId: number): Observable<boolean> {
    const params = new HttpParams().set('branchId', branchId).set('maintenanceOrderClassId', maintenanceOrderClassId);
    return this._httpClient.get<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceOrder.HasExistingMaintenanceOrderAsync}`,
      { params: params }
    );
  }

  importMaintenanceOrders(maintenanceOrderImports: Array<MaintenanceOrderImportModel>): Observable<LoadPreInvoicingModel> {
    return this._httpClient.post<LoadPreInvoicingModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceOrder.ImportMaintenanceOrdersAsync}`,
      maintenanceOrderImports
    );
  }

  updateMaintenanceOrder(maintenanceOrderContractModel: MaintenanceOrderModel): Observable<MaintenanceOrderModel> {
    return this._httpClient.put<MaintenanceOrderModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceOrder.UpdateAsync}`,
      maintenanceOrderContractModel);
  }

  validateKmHoromValue(maintenanceOrderId: number, currentKmHoromValue: number): Observable<boolean> {
    const params = new HttpParams().set('maintenanceOrderId', maintenanceOrderId).set('currentKmHoromValue', currentKmHoromValue);
    return this._httpClient.get<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceOrder.ValidateKmHoromValueAsyncAsync}`,
      { params: params }
    );
  }

  validateMaintenanceOrderOrigin(maintenanceOrderId: number): Observable<boolean> {
    const params = new HttpParams().set('maintenanceOrderId', maintenanceOrderId);
    return this._httpClient.get<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceOrder.ValidateMaintenanceOrderOriginAsync}`,
      { params: params }
    );
  }

}
